/* eslint-disable max-lines */
import * as types from '../mutation-types';

import { apolloClient } from '@/apollo';
import {
  AuthenticateDocument,
  UpdateUserDocument,
  UpdateUserImageDocument,
  DeleteUserDocument,
  LeaveProjectDocument,
  UserDocument,
  AuthenticateMutationVariables,
  AuthenticateMutation,
} from '@/graphql/operations.gen';
import { getTracker } from '@/tracking/trackingPlugin';
import { UserEvent } from '@/tracking/trackingTypes';

import { getInstance } from '@/auth/index';
import { broadcastLogout } from '@/broadcast';
import { Mutation, Action, Getter } from 'vuex';
import { RootState, UserState } from '../types';
import { LeaseState } from '@common/license/types';
import { UserAuthStatus } from '@common/types';

import { logout as helpscoutLogout } from '@/helpscout';
import { getLeaseManager } from '@/license';
import config from '@/config/client';
import { localConfigReactive } from '@/apollo/cache';
import { assocPath, dissocPath } from 'ramda';

// initial state
const initialState: UserState = {
  inviteToken: null,
  user: null,
  authUser: null,
  localConfig: {},
  loggedIn: false,
  licenseCheckInProgress: false,
  kickLeaseInProgress: false,
  authenticationInProgress: false,
  authenticationFailed: false,
  authStatus: null,
  canHaveTrial: false,
  lease: null,
  leaseState: LeaseState.Undefined,
  subscriptionsAvailable: undefined,
};

// getters
const getters: { [key: string]: Getter<UserState, RootState> } = {
  localConfig(state) {
    return state.localConfig;
  },

  activityData(state) {
    return {
      __typename: 'User',
      id: state.user.id,
      firstname: state.user.firstname,
      lastname: state.user.lastname,
      fullname: state.user.fullname,
      image: state.user.image,
      email: state.user.email,
      language: state.user.locale,
    };
  },

  isLimited(state) {
    return state.leaseState === LeaseState.NoMatchingLicense;
  },

  startUpInProgress(state) {
    return state.authenticationInProgress || state.licenseCheckInProgress || state.kickLeaseInProgress;
  },

  remainingTrialDays(state) {
    const remainingTrialDays = state.leaseState === LeaseState.Ok && state.lease?.license?.remaining_trial_days;

    return typeof remainingTrialDays === 'undefined' ? false : remainingTrialDays;
  },

  leaseBlockable(state) {
    const leaseBlockable = state.leaseState === LeaseState.Ok && state.lease?.license?.multiuser === true;

    return typeof leaseBlockable === 'undefined' ? false : leaseBlockable;
  },

  leaseBlockedUntil(state) {
    const leaseBlockedUntil = state.lease?.extended_expiration_at;

    return typeof leaseBlockedUntil === 'undefined' ? null : leaseBlockedUntil;
  },
};

// actions
const actions: { [key: string]: Action<UserState, RootState> } = {
  /**
   * Revoke.
   */
  revoke({ commit }) {
    commit(types.USER_REVOKE);
  },

  /**
   * Logout user.
   *
   */
  logout({ commit, rootGetters }, disableBroadcast = false) {
    commit(types.USER_LOGOUT);

    if (!disableBroadcast) {
      broadcastLogout();
    }

    // Clear cached data without reloading active queries
    apolloClient.clearStore();

    getTracker().trackUserLoggedOut();

    const instance = getInstance();

    if (!rootGetters['tracking/analyticsConsent']) {
      // Clear helpscout data that is not securely associated with a user
      helpscoutLogout();
    }

    getLeaseManager(this).returnLease();

    instance.logout({
      returnTo: `${config.acoHost}/cloud-logout`,
    });
  },

  /**
   * Login user after we've received a user from the cloud.
   *
   */
  login({ commit }, data) {
    commit(types.USER_SET_DATA, data);
    commit(types.USER_INIT_LOCAL_CONFIG);

    getTracker().trackUserLoggedIn(data);
  },

  /**
   * Authenticate the user in the Cloud.
   *
   */
  async authenticate({ commit }, { firstname, lastname, email, mayReceiveEmails, locale }) {
    commit(types.USER_AUTHENTICATION_STARTED);

    const response = await apolloClient
      .mutate<AuthenticateMutation, AuthenticateMutationVariables>({
        mutation: AuthenticateDocument,
        variables: {
          firstname,
          lastname,
          email,
          mayReceiveEmails,
          locale,
        },
        update(cache, result) {
          // Update the user query so that the Base component has access to user data
          // immediately after authenticate
          cache.writeQuery({
            query: UserDocument,
            data: {
              user: result.data.authenticate?.user,
            },
          });
        },
      })
      .catch(() => {
        commit(types.USER_AUTHENTICATION_FAILED);
      });

    if (response) {
      commit(types.USER_AUTHENTICATION_COMPLETED, {
        status: response.data.authenticate.status,
      });

      return response.data.authenticate;
    }

    return null;
  },

  update({ commit, state }, data) {
    commit(types.USER_SET_DATA, { ...state.user, ...data });

    return apolloClient.mutate({
      mutation: UpdateUserDocument,
      variables: {
        ...data,
      },
    });
  },

  updateImage({}, { file }) {
    return apolloClient.mutate({
      mutation: UpdateUserImageDocument,
      variables: {
        file,
      },
    });
  },

  delete({ dispatch, commit }) {
    commit(types.USER_DELETE, {
      meta: {
        analytics: [['event', 'user', 'delete']],
      },
    });

    return apolloClient
      .mutate({
        mutation: DeleteUserDocument,
      })
      .then(() => {
        getTracker().track(UserEvent.AccountDeleted);

        dispatch('logout');
      });
  },

  leaveProject({}, variables) {
    return apolloClient.mutate({
      mutation: LeaveProjectDocument,
      variables,
      refetchQueries: ['User'], // TODO: check if needed or how to make without refetch
    });
  },

  setAuth0Data({ commit }, data) {
    commit(types.USER_SET_AUTH_DATA, data);
  },

  setLocalConfig({ commit }, data) {
    commit(types.USER_SET_LOCAL_CONFIG, data);
  },

  unsetLocalConfig({ commit }, keysToRemove: Array<string>) {
    commit(types.USER_UNSET_LOCAL_CONFIG, keysToRemove);
  },

  setLeaseData({ commit, dispatch, state }, data) {
    commit(types.USER_SET_LEASE_DATA, data);

    getTracker().trackUpdateLeaseState(data);

    if (data.leaseState === LeaseState.SelfOccupiedSeat && !state.kickLeaseInProgress) {
      dispatch(
        'modal/show',
        {
          type: 'kickoutLeaseCard',
        },
        { root: true }
      );
    }
  },

  updateAiSplashScreenCooldown({ dispatch }) {
    const cooldownMinutes = 30;
    const expiresIn = Date.now() + cooldownMinutes * 60000;

    dispatch('setLocalConfig', {
      aiSplashScreenCooldown: expiresIn,
    });
  },

  updatePaperSearchSplashOverlayCooldown({ dispatch }) {
    const cooldownMinutes = 30;
    const expiresIn = Date.now() + cooldownMinutes * 60000;

    dispatch('setLocalConfig', {
      paperSearchSplashOverlayCooldown: expiresIn,
    });
  },
};

// mutations
const mutations: { [key: string]: Mutation<UserState> } = {
  [types.USER_LOGOUT](state) {
    state.user = null;
    state.loggedIn = false;
  },

  [types.USER_REVOKE](state) {
    apolloClient.cache.reset();
    state.loggedIn = false;
    state.user = null;
  },

  [types.USER_DELETE]() {},

  [types.USER_SET_INVITE_TOKEN](state, token) {
    state.inviteToken = token;
  },

  [types.USER_SET_DATA](state, data) {
    state.user = data;
    state.loggedIn = true;
  },

  [types.USER_SET_AUTH_DATA](state, data) {
    state.authUser = data;
  },

  [types.USER_INIT_LOCAL_CONFIG](state) {
    const local = localStorage.getItem(`atlasticloud${state.user.id}config`);

    if (local) {
      const parsed = <{ [key: string]: any }>JSON.parse(local);
      state.localConfig = parsed;

      localConfigReactive(parsed);
    }
  },

  [types.USER_UNSET_LOCAL_CONFIG](state, keysToRemove: Array<string | Array<string>>) {
    let newData = { ...state.localConfig };

    keysToRemove.forEach((key) => {
      newData = dissocPath(Array.isArray(key) ? key : key.split('.'), newData);
    });

    state.localConfig = newData;

    localStorage.setItem(`atlasticloud${state.user.id}config`, JSON.stringify(newData));
    localConfigReactive(newData);
  },

  /**
   * Set the local config
   *
   * @param data an object where keys->values map to the localConfig object
   *                if the key is dot separated, it will be translated to
   *                a deep path inside the localConfig
   */
  [types.USER_SET_LOCAL_CONFIG](state, data: Record<string, any>) {
    let local = localStorage.getItem(`atlasticloud${state.user.id}config`);

    if (local) {
      local = JSON.parse(local);
    }

    let newData = local || {};

    Object.entries(data).forEach(([path, value]) => {
      newData = assocPath(path.split('.'), value, newData);
    });

    state.localConfig = newData;
    localStorage.setItem(`atlasticloud${state.user.id}config`, JSON.stringify(newData));
    localConfigReactive(newData);
  },
  [types.USER_KICK_LEASE_STARTED](state) {
    state.kickLeaseInProgress = true;
  },
  [types.USER_KICK_LEASE_COMPLETED](state) {
    state.kickLeaseInProgress = false;
  },
  [types.USER_LICENSE_CHECK_STARTED](state) {
    state.licenseCheckInProgress = true;
  },
  [types.USER_LICENSE_CHECK_COMPLETED](state) {
    state.licenseCheckInProgress = false;
  },
  [types.USER_AUTHENTICATION_STARTED](state) {
    state.authenticationInProgress = true;
  },
  [types.USER_AUTHENTICATION_COMPLETED](state, { status }: { status: UserAuthStatus }) {
    state.authenticationInProgress = false;
    state.authStatus = status;
  },
  [types.USER_AUTHENTICATION_FAILED](state) {
    state.authenticationInProgress = false;
    state.authenticationFailed = true;
  },
  [types.USER_CAN_HAVE_TRIAL](state, data) {
    state.canHaveTrial = data;
  },

  [types.USER_SET_LEASE_DATA](state, data) {
    state.lease = data.lease;
    state.leaseState = data.leaseState;
  },

  [types.USER_SET_SUBSCRIPTIONS_AVAILABILITY](state, status: boolean) {
    state.subscriptionsAvailable = status;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
